import { useState } from "react";
import { UploadSimple, FileArrowUp, Warning, ArrowUp, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { TASK_CODES, codeLabel } from "../pages/GuidelinesPage";

const STATUS_STYLES = {
  new: { label: "New", cls: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" },
  changed: { label: "Update — staff will re-sign", cls: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]" },
  same: { label: "Unchanged", cls: "border-stone-300 bg-stone-100 text-stone-500" },
};

export const GuidelineImportDialog = ({ onApplied }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [approved, setApproved] = useState({});
  const [codesOpen, setCodesOpen] = useState({});

  const reset = () => { setResult(null); setFile(null); setApproved({}); setCodesOpen({}); };

  const upload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/guidelines/import", fd, { timeout: 180000 });
      setResult(data);
      const auto = {};
      for (const d of data.drafts) auto[d.id] = d.status !== "same";
      setApproved(auto);
      if (!data.drafts.length) toast.error("The AI found no policies to split out of that document");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const patchDraft = (id, patch) =>
    setResult((r) => ({ ...r, drafts: r.drafts.map((d) => (d.id === id ? { ...d, ...patch } : d)) }));

  const removeDraft = (id) => {
    setResult((r) => ({ ...r, drafts: r.drafts.filter((d) => d.id !== id) }));
    setApproved((s) => { const n = { ...s }; delete n[id]; return n; });
  };

  const mergeUp = (idx) => {
    setResult((r) => {
      const drafts = [...r.drafts];
      const cur = drafts[idx];
      const prev = drafts[idx - 1];
      const merged = {
        ...prev,
        content: `${prev.content}\n\n${cur.content}`,
        task_codes: [...new Set([...(prev.task_codes || []), ...(cur.task_codes || [])])],
        status: prev.status === "same" ? "changed" : prev.status,
      };
      drafts.splice(idx - 1, 2, merged);
      return { ...r, drafts };
    });
  };

  const apply = async () => {
    const drafts = result.drafts.filter((d) => approved[d.id]);
    if (!drafts.length) { toast.error("Approve at least one guideline first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/guidelines/import-apply", { drafts });
      toast.success(`Done — ${data.created} created, ${data.updated} updated (re-sign required)`);
      setOpen(false);
      reset();
      onApplied?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const approvedCount = Object.values(approved).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="guideline-import-button">
          <UploadSimple size={16} className="mr-1.5" /> Import from Document
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="guideline-import-dialog">
        <DialogHeader>
          <DialogTitle>Import guidelines from a policy document</DialogTitle>
          <DialogDescription>
            Upload your policy manual (PDF, Word, or text). The AI splits it into individual guidelines with suggested task codes —
            you review, edit, merge, and approve each one before anything is saved. Re-importing an updated manual bumps versions on changed policies so staff re-sign.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4" data-testid="guideline-import-upload-step">
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-stone-300 bg-accent/40 p-8 text-center hover:border-primary">
              <FileArrowUp size={28} className="text-stone-400" />
              <span className="text-sm font-medium text-stone-700">{file ? file.name : "Choose a PDF, Word, or text file"}</span>
              <span className="text-xs text-stone-400">Scans/photos of paper aren't supported yet — the file needs real text</span>
              <input type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="guideline-import-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <Button onClick={upload} disabled={busy || !file} className="w-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="guideline-import-upload-button">
              {busy ? "AI is reading the document..." : "Upload & Split into Guidelines"}
            </Button>
            {busy && <p className="text-center text-xs text-stone-400">Usually under a minute for a ~10 page document.</p>}
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="guideline-import-review">
            {(result.questions || []).length > 0 && (
              <div className="rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-4" data-testid="guideline-import-questions">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-[#8a5d14]"><Warning size={15} /> The AI flagged these:</p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-stone-600">
                  {result.questions.map((q) => <li key={q}>{q}</li>)}
                </ul>
              </div>
            )}
            {(result.missing || []).length > 0 && (
              <div className="rounded-lg border border-stone-300 bg-stone-50 p-4" data-testid="guideline-import-missing">
                <p className="text-sm font-semibold text-stone-700">Existing guidelines not found in this document:</p>
                <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-xs text-stone-500">
                  {result.missing.map((m) => <li key={m.id}>{m.title}</li>)}
                </ul>
                <p className="mt-2 text-[11px] text-stone-400">Nothing is removed automatically — edit or deactivate these from the guidelines list if they've been retired.</p>
              </div>
            )}
            {result.drafts.length === 0 && (
              <p className="py-6 text-center text-sm text-stone-500">No guidelines to review.</p>
            )}
            {result.drafts.map((d, idx) => {
              const st = STATUS_STYLES[d.status] || STATUS_STYLES.new;
              return (
                <div key={d.id} className={`rounded-lg border bg-white ${approved[d.id] ? "border-stone-200" : "border-stone-200 opacity-60"}`} data-testid={`guideline-import-draft-${d.id}`}>
                  <div className="flex flex-wrap items-center gap-2 border-b border-stone-100 px-4 py-2.5">
                    <input type="checkbox" checked={!!approved[d.id]} data-testid={`guideline-import-approve-${d.id}`}
                      onChange={(e) => setApproved((s) => ({ ...s, [d.id]: e.target.checked }))} />
                    <Input value={d.title} onChange={(e) => patchDraft(d.id, { title: e.target.value })}
                      className="h-8 min-w-0 flex-1 border-stone-300 bg-white text-sm font-semibold" data-testid={`guideline-import-title-${d.id}`} />
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${st.cls}`} data-testid={`guideline-import-status-${d.id}`}>{st.label}</span>
                    <div className="flex gap-1">
                      {idx > 0 && (
                        <button onClick={() => mergeUp(idx)} title="Merge into the guideline above"
                          className="rounded-md border border-stone-200 p-1.5 text-stone-500 hover:bg-accent" data-testid={`guideline-import-merge-${d.id}`}>
                          <ArrowUp size={14} />
                        </button>
                      )}
                      <button onClick={() => removeDraft(d.id)} title="Remove this draft"
                        className="rounded-md border border-stone-200 p-1.5 text-stone-500 hover:text-[#C0392B]" data-testid={`guideline-import-delete-${d.id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 px-4 py-3">
                    <Textarea rows={5} value={d.content} onChange={(e) => patchDraft(d.id, { content: e.target.value })}
                      className="border-stone-300 bg-white text-xs leading-relaxed" data-testid={`guideline-import-content-${d.id}`} />
                    <div>
                      <div className="flex flex-wrap items-center gap-1">
                        {(d.task_codes || []).map((c) => (
                          <span key={c} className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground">{codeLabel(c)}</span>
                        ))}
                        <button onClick={() => setCodesOpen((s) => ({ ...s, [d.id]: !s[d.id] }))}
                          className="text-[11px] font-medium text-primary hover:underline" data-testid={`guideline-import-codes-toggle-${d.id}`}>
                          {codesOpen[d.id] ? "Done" : "Edit task codes"}
                        </button>
                      </div>
                      {codesOpen[d.id] && (
                        <div className="mt-2 grid grid-cols-2 gap-1 rounded-lg border border-stone-200 bg-accent/30 p-2" data-testid={`guideline-import-codes-${d.id}`}>
                          {TASK_CODES.map(([code, label]) => (
                            <label key={code} className="flex items-center gap-1.5 text-[11px] text-stone-600">
                              <input type="checkbox" checked={(d.task_codes || []).includes(code)}
                                onChange={(e) => patchDraft(d.id, { task_codes: e.target.checked ? [...(d.task_codes || []), code] : (d.task_codes || []).filter((c) => c !== code) })}
                                data-testid={`guideline-import-code-${d.id}-${code}`} />
                              {code} {label}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-between gap-3 pt-2">
              <Button variant="outline" onClick={reset} disabled={busy} className="border-stone-300 text-stone-500" data-testid="guideline-import-back-button">
                Start over
              </Button>
              <Button onClick={apply} disabled={busy || approvedCount === 0} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="guideline-import-apply-button">
                {busy ? "Saving..." : `Save ${approvedCount} guideline${approvedCount === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
